import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Command } from "cmdk";
import {
  ArrowRight,
  Mail,
  Linkedin,
  Sparkles,
  LayoutList,
  Compass,
  User,
  Wrench,
  Box,
  Command as CommandIcon,
  Beaker,
} from "lucide-react";
import { NAV_LINKS } from "@/content";
import { CASE_LIST, CASES } from "@/case-content";

const SECTION_ICON = {
  origin: Compass,
  hats: User,
  proof: LayoutList,
  operate: Wrench,
  signal: Sparkles,
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => {
          if (!v) window.posthog?.capture("palette_opened", { method: "keyboard" });
          return !v;
        });
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const goSection = (id) => () => {
    close();
    if (location.pathname === "/") {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) {
          const top = el.getBoundingClientRect().top + window.scrollY - 48;
          window.__lenis.scrollTo(top, { duration: 1.0 });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 140);
    } else {
      navigate(`/#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  };

  const goRoute = (to) => () => {
    close();
    setTimeout(() => navigate(to), 80);
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); window.posthog?.capture("palette_opened", { method: "button" }); }}
        data-cursor="magnetic"
        data-cursor-label="JUMP ⌘"
        data-testid="palette-trigger"
        className="fixed bottom-4 left-4 z-40 hidden md:inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#1A1A1A] dark:bg-[#E6EDF3] text-white dark:text-[#0D1117] border border-white/10 dark:border-black/10 shadow-[0_8px_32px_rgba(0,0,0,0.18)] hover:opacity-85 transition-opacity"
        aria-label="Open menu"
      >
        <CommandIcon size={14} strokeWidth={2.2} />
        <span className="font-mono text-[11px] tracking-[0.22em] uppercase">
          <span className="hidden md:inline">⌘ K</span>
          <span className="md:hidden">Menu</span>
        </span>
        <span className="hidden lg:inline font-mono text-[10px] tracking-[0.22em] uppercase text-paper/60">
          Navigate
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-[14vh] px-4"
          data-testid="palette-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          onClick={close}
        >
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm animate-[fadeIn_.18s_ease-out]" />

          <div
            className="relative w-full max-w-[600px] rounded-2xl overflow-hidden shadow-2xl border border-black/10 bg-white animate-[popIn_.22s_cubic-bezier(.16,1,.3,1)]"
            onClick={(e) => e.stopPropagation()}
            data-cursor="text"
          >
            <Command
              label="Command palette"
              className="w-full"
              filter={(value, search) =>
                value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
              }
            >
              <div className="flex items-center gap-3 px-5 py-4 border-b border-black/8">
                <CommandIcon size={16} className="text-mute" />
                <Command.Input
                  autoFocus
                  placeholder="Search · navigate · jump to a case study or the lab…"
                  className="flex-1 bg-transparent outline-none placeholder:text-mute/70 text-[15px] text-ink font-sans"
                  data-testid="palette-input"
                />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-mute">
                  ESC
                </span>
              </div>

              <Command.List className="max-h-[60vh] overflow-y-auto p-2">
                <Command.Empty className="px-4 py-8 text-center text-mute font-sans text-sm">
                  Nothing matched. Try "lab" or "luna" or "email".
                </Command.Empty>

                <PalGroup heading="Navigate · Sections">
                  {NAV_LINKS.map((l) => {
                    const Icon = SECTION_ICON[l.id] || Box;
                    return (
                      <PalItem
                        key={l.id}
                        value={`navigate ${l.id} ${l.label}`}
                        onSelect={goSection(l.id)}
                        testId={`palette-go-${l.id}`}
                        Icon={Icon}
                        label={l.label}
                      />
                    );
                  })}
                </PalGroup>

                <PalDivider />

                <PalGroup heading="Open · Pages">
                  <PalItem
                    value="lab playground experiments ai"
                    onSelect={goRoute("/lab")}
                    testId="palette-open-lab"
                    Icon={Beaker}
                    label="The Lab"
                    hint="AI experiments, agents, songs"
                  />
                  {CASE_LIST.map((slug) => (
                    <PalItem
                      key={slug}
                      value={`case ${slug} ${CASES[slug].name}`}
                      onSelect={goRoute(`/case/${slug}`)}
                      testId={`palette-open-case-${slug}`}
                      Icon={LayoutList}
                      label={CASES[slug].name}
                      hint={CASES[slug].domain}
                    />
                  ))}
                </PalGroup>

                <PalDivider />

                <PalGroup heading="Contact">
                  <PalItem
                    value="contact message conversation email"
                    onSelect={() => {
                      close();
                      window.dispatchEvent(new CustomEvent("erpik:open-contact", { detail: { subject: "General enquiry" } }));
                    }}
                    testId="palette-email"
                    Icon={Mail}
                    label="Start a conversation"
                  />
                  <PalItem
                    value="linkedin connect"
                    onSelect={() => {
                      window.open(
                        "https://linkedin.com/in/samerpik/",
                        "_blank",
                        "noopener"
                      );
                    }}
                    testId="palette-linkedin"
                    Icon={Linkedin}
                    label="Connect on LinkedIn"
                  />
                </PalGroup>
              </Command.List>

              <div className="flex items-center justify-between px-4 py-2.5 border-t border-black/8 bg-paper">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-mute">
                  Press ⏎ to select
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-mute">
                  Sam · Erpik
                </span>
              </div>
            </Command>
          </div>
        </div>
      )}
    </>
  );
}

function PalGroup({ heading, children }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:tracking-[0.22em] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-mute"
    >
      {children}
    </Command.Group>
  );
}

function PalDivider() {
  return <div className="mx-3 my-2 h-[1px] bg-black/8" />;
}

function PalItem({ value, onSelect, testId, Icon, label, hint }) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      data-testid={testId}
      className="group flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer aria-selected:bg-paper data-[selected=true]:bg-paper transition-colors"
    >
      <Icon size={16} className="text-mute group-data-[selected=true]:text-ink" />
      <span className="flex-1 text-[14px] text-ink">{label}</span>
      {hint && (
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-mute">
          {hint}
        </span>
      )}
      <ArrowRight
        size={14}
        className="opacity-0 group-data-[selected=true]:opacity-100 text-ink"
      />
    </Command.Item>
  );
}
